import { useEffect, useRef, useState } from "react";
import api, { fmtCents } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import BankSync from "@/components/BankSync";
import { useHouseholdMode } from "@/hooks/useHouseholdMode";

export default function Transactions() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const fileRef = useRef();
  const { enabled: hhMode, param } = useHouseholdMode();

  const load = async () => {
    const [{ data: tx }, { data: c }] = await Promise.all([
      api.get("/transactions", { params: { limit: 200, ...param } }),
      api.get("/categories"),
    ]);
    setItems(tx);
    setCats(c);
  };

  useEffect(() => { load(); }, [hhMode]); // eslint-disable-line

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const form = new FormData();
    form.append("file", f);
    try {
      const { data } = await api.post("/transactions/upload_csv", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.inserted} transactions`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      e.target.value = "";
    }
  };

  const updateCat = async (id, category) => {
    await api.patch(`/transactions/${id}`, { category, remember_category: true });
    setItems((cur) => cur.map((t) => (t.id === id ? { ...t, category } : t)));
    toast.success("Category updated — we'll remember for this merchant");
  };

  // Group by date
  const groups = items.reduce((acc, t) => {
    (acc[t.date] = acc[t.date] || []).push(t);
    return acc;
  }, {});

  return (
    <div className="px-6 pt-10 pb-6">
      <div className="flex items-center justify-between">
        <h1 className="font-manrope text-2xl font-extrabold tracking-tight text-slate-900">Transactions</h1>
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept=".csv" onChange={upload} className="hidden" data-testid="csv-file-input" />
          <button onClick={() => fileRef.current?.click()} data-testid="upload-csv-btn" className="w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors" aria-label="Upload CSV">
            <Upload size={17} strokeWidth={1.75} />
          </button>
          <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild>
              <button data-testid="add-tx-btn" className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center hover:bg-slate-800 transition-colors">
                <Plus size={17} strokeWidth={2} />
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-sm rounded-3xl">
              <DialogHeader><DialogTitle className="font-manrope">Add transaction</DialogTitle></DialogHeader>
              <AddForm cats={cats} onDone={() => { setShowAdd(false); load(); }} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <p className="text-slate-500 text-sm mt-2">Connect a bank, upload a CSV, or add manually. Categories are auto-detected.</p>

      <div className="mt-6">
        <BankSync onSynced={load} />
      </div>

      {items.length === 0 && (
        <div className="mt-16 text-center text-slate-500">
          <div className="text-4xl mb-3">✦</div>
          <div className="font-medium">No transactions yet</div>
          <div className="text-sm mt-1">Upload a CSV or add your first transaction.</div>
        </div>
      )}

      <div className="mt-6 space-y-6">
        {Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0])).map(([date, txs]) => (
          <div key={date}>
            <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">{date}</div>
            <div className="mt-2 space-y-2">
              {txs.map((t) => (
                <div key={t.id} className="flex items-center justify-between p-3 rounded-xl border border-slate-200" data-testid={`tx-${t.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 text-sm truncate">{t.merchant}</div>
                    <Select value={t.category} onValueChange={(v) => updateCat(t.id, v)}>
                      <SelectTrigger className="mt-1 h-7 w-auto inline-flex text-[11px] px-2 py-0 rounded-full bg-slate-100 border-0 text-slate-600 font-semibold uppercase tracking-wider">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className={`font-manrope font-bold tabular-nums ${t.is_income ? "text-emerald-600" : "text-slate-900"}`}>
                    {t.is_income ? "+" : "-"}{fmtCents(t.amount)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AddForm({ cats, onDone }) {
  const [d, setD] = useState({ date: new Date().toISOString().slice(0, 10), merchant: "", amount: 0, category: "Other", is_income: false });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/transactions", d);
      toast.success("Added");
      onDone();
    } catch { toast.error("Failed to add"); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="space-y-3 mt-2">
      <input data-testid="add-date-input" type="date" required value={d.date} onChange={(e) => setD({ ...d, date: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none" />
      <input data-testid="add-merchant-input" required placeholder="Merchant" value={d.merchant} onChange={(e) => setD({ ...d, merchant: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none" />
      <input data-testid="add-amount-input" required type="number" step="0.01" min="0" placeholder="Amount" value={d.amount} onChange={(e) => setD({ ...d, amount: Number(e.target.value) })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none" />
      <Select value={d.category} onValueChange={(v) => setD({ ...d, category: v })}>
        <SelectTrigger className="w-full"><SelectValue placeholder="Category" /></SelectTrigger>
        <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
      </Select>
      <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={d.is_income} onChange={(e) => setD({ ...d, is_income: e.target.checked })} /> This is income</label>
      <button data-testid="add-tx-submit" disabled={busy} className="w-full bg-slate-900 text-white rounded-full py-3 font-semibold hover:bg-slate-800 transition-colors">{busy ? "Adding…" : "Add transaction"}</button>
    </form>
  );
}
